import React, {useCallback} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {IValuesToQuery, useQuery} from '../../hocs/hooks'
import {Field, Form} from 'react-final-form'
import Times from '../../../assets/icons/Times.svg'
import {
  areaInPrefectureList,
  areaList,
  AreaType,
  CURRENT_LOCATION,
  getPrefecture,
  Locale,
  prefectureList,
  PrefectureType,
  StationType
} from '../../../config/constants'
import {areaMessages, } from '../../../config/commonMessages'
import BackDrop from '../../containers/BackDrop'
import {initialArea} from '../../maps/mapsUtil'
import searchStyles from './CoverPC.scss'
import styles from './BasicSearch.scss'
import FaMapMarkerAlt from '../../../assets/icons/FaMapMarkerAlt.svg'
import CheckIn from '../../../assets/icons/vanshare/SignIn.svg'
import CheckOut from '../../../assets/icons/vanshare/SignOut.svg'
import Question from '../../../assets/icons/vanshare/Question.svg'
import Search from '../../../assets/icons/vanshare/Search.svg'
import DateField from '../../fields/custom/DateField'
import { dateFromQuery, dateToQuery, maxCalendarYear, thisYear } from '../../../helpers/dateUtil'
import LocaleText from '../../common/LocaleText'
import { intFromQuery } from '../../../helpers/coreUtil'
import { ParsedQuery } from 'query-string'

export const stationCarQueryToValues = (
  {
    checkIn,
    checkOut,
    page,
    zoom,
    ...query
  }: ParsedQuery<string>) => ({
  checkIn: dateFromQuery(checkIn),
  checkOut: dateFromQuery(checkOut),
  page: intFromQuery(page, 1) - 1,
  zoom: intFromQuery(zoom),
  ...query
})

export const stationCarValuesToQuery: IValuesToQuery<typeof stationCarQueryToValues> = (
  {
    checkIn,
    checkOut,
    page,
    zoom,
    ...query
  }) => ({
  checkIn: dateToQuery(checkIn),
  checkOut: dateToQuery(checkOut),
  page: page === 0 ? undefined : page + 1,
  zoom,
  ...query
})

export interface ISearchForm {
  prefecture?: PrefectureType
  area?: AreaType
  checkIn?: Date
  checkOut?: Date
  zoom?: number
}

const BasicSearch = ({stationType, onClose, onSearchSubmit, locale, area, prefecture}: {
  stationType: StationType
  onClose: () => void
  onSearchSubmit: (values: ISearchForm) => void
  locale: Locale
  area: string
  prefecture?: PrefectureType
}) => {
  const initialValues = { ...useQuery(stationCarQueryToValues), ...initialArea(area, prefecture) }
  const onSubmit = useCallback((values: any) => {
    onSearchSubmit(values)
    onClose()
  }, [onClose, onSearchSubmit])
  const intl = useIntl()
  return <Form onSubmit={onSubmit} initialValues={initialValues}>
    {({handleSubmit}) => <>
      <form onSubmit={handleSubmit}
            className={searchStyles.searchBox}
            id="refineSearch">
        <BackDrop onBackgroundClicked={onClose}>
          <div tabIndex={-1} role="dialog" className={['m-2', styles.modalPanel].join(' ')}>
            <div className="modal-content w-100">
              <div className="modal-header py-4">
                <Times className={styles.cross} onClick={onClose}/>

                <div className="w-100 text-center">
                  <h3 className={['m-0 font-weight-medium'].join(' ')}>
                    {stationType === StationType.car
                      ? <FormattedMessage id="carsSearchBasic.title" defaultMessage="Car search"/>
                      : <FormattedMessage id="stationsSearchBasic.title" defaultMessage="Station search"/>}
                  </h3>
                </div>
              </div>

              <div className={['modal-body', styles.modalBody].join(' ')}>
                <div className="pb-3">
                  <div className={['mb-1 font-weight-bold'].join(' ')}>
                    <FaMapMarkerAlt
                      className={['fa mb-1 mr-1', stationType !== StationType.car ? 'text-primary' : 'text-van'].join(' ')}/>
                    <FormattedMessage id="carsSearch.area" defaultMessage="Area"/>
                  </div>
                  <div className={['position-relative', styles.inputForm].join(' ')}>
                    <Field component="select" {...{ name: 'area' }}
                          className="pl-3 custom-select h-100">
                      <FormattedMessage id="stationsSearch.allCountry">
                        {message => <option value="">
                          {message}
                        </option>
                        }
                      </FormattedMessage>
                      <FormattedMessage id="stationsSearch.currentLocation">
                        {message => <option value={CURRENT_LOCATION}>
                          {message}
                        </option>
                        }
                      </FormattedMessage>
                      {areaList.flatMap(area => {
                        const prefectures = areaInPrefectureList[area].map(
                          prefecture => ({ text: `　${getPrefecture(locale, prefecture)}`, value: prefecture })
                        )
                        return [{ text: intl.formatMessage({ ...areaMessages[area] }), value: area }, ...prefectures]
                      }).map((x, i) => <option key={i} value={x.value}>
                              {x.text}
                            </option>)
                      }
                    </Field>
                  </div>
                </div>
                <div className="row mx-0 pb-3">
                  <div className="flex-grow-0 w-50">
                    <div className={['mb-1 font-weight-bold'].join(' ')}>
                      <CheckIn
                        className={['fa mb-1 mr-1 font-size-18', stationType !== StationType.car ? 'text-primary' : 'text-van']
                          .join(' ')}
                      />
                      {stationType === StationType.car
                        ? <FormattedMessage id="carsSearch.pickup" defaultMessage="Pick up day"/>
                        : <FormattedMessage id="stationsSearch.checkIn"/>}
                    </div>
                    <Field
                      component={DateField}
                      name="checkIn"
                      className={['px-2', styles.checkIn].join(' ')}
                      isDisabled={
                        (clientDate: Date) => clientDate.getTime() < new Date(Date.now()).setHours(0, 0, 0, 0)
                      }
                      minYear={thisYear}
                      maxYear={maxCalendarYear}
                    />
                  </div>

                  <div className="flex-grow-0 w-50">
                    <div className={['mb-1 font-weight-bold'].join(' ')}>
                      <CheckOut
                        className={['fa mb-1 mr-1 font-size-18', stationType !== StationType.car ? 'text-primary' : 'text-van']
                          .join(' ')}
                      />
                      {stationType !== StationType.car
                        ? <FormattedMessage id="stationsSearch.checkOut"/>
                        : <FormattedMessage id="carsSearch.return" />}
                    </div>
                    <Field
                      component={DateField}
                      name="checkOut"
                      className={['px-2', styles.checkOut].join(' ')}
                      alignRight
                      isDisabled={
                        (clientDate: Date) => clientDate.getTime() < new Date(Date.now()).setHours(0, 0, 0, 0)
                      }
                      minYear={thisYear}
                      maxYear={maxCalendarYear}
                    />
                  </div>
                </div>
                <div className="pb-3">
                  <button
                    type="submit"
                    className={[
                      'btn font-weight-bold',
                      styles.button,
                      stationType !== StationType.car ? 'btn-primary' : 'btn-van'
                    ].join(' ')}
                  >
                    <Search className="fa mb-1 mr-1"/>
                    <FormattedMessage id="search.btnLabel"/>
                  </button>
                </div>
                <div>
                  <a
                    href="https://persistent-direction-0f1.notion.site/333beeb0fe76453892cbef5f7456a12f"
                    target="_blank"
                    className={[
                      'w-100 d-inline-block pt-1 font-size-12 underline text-right',
                      stationType !== StationType.car ? 'text-primary' : 'text-van'
                    ].join(' ')}
                  >
                    <Question className="fa mb-1 mr-1"/>
                    <LocaleText ja="現在地から検索できない場合" en="If you cannot search from your current location"/>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </BackDrop>
      </form>
    </>}
  </Form>
}

BasicSearch.displayName = 'BasicSearch'
export default BasicSearch
